import { format, isToday, isYesterday } from "date-fns"

// Pure helpers for the chat-style comment feed. Kept free of React so they can be unit-tested
// directly and reused by the panel and the toolbar without UI dependencies.

export function getCreatedCommentId(created) {
    if (!created) return null
    if (typeof created === "string") return created
    return created._id ?? created.id ?? null
}

export function commentExistsInThreads(threads = [], commentId) {
    if (!commentId) return false
    for (const thread of threads) {
        if (thread.id === commentId || thread.threadId === commentId) return true
        for (const reply of thread.replies ?? []) {
            if (reply.id === commentId) return true
        }
    }
    return false
}

// Compute the stable filter value for a comment thread's anchor. This MUST stay in sync with the
// toolbar's target dropdown values (`share-link-comment-toolbar.js` imports this same helper) so
// that selecting a target in the toolbar filters the feed to the matching threads.
//   schedule -> "schedule", intro -> "intro", task -> `task:${taskIndex}`.
export function getTargetValue(anchor) {
    if (!anchor) return null
    if (anchor.type === "task") {
        const index = Number(anchor.taskIndex)
        if (!Number.isInteger(index)) return null
        return `task:${index}`
    }
    if (anchor.type === "schedule" || anchor.type === "intro") return anchor.type
    return null
}

// Apply the toolbar's status + target filters to the already visibility-filtered threads.
export function getVisibleThreads(threads = [], { statusFilter = "open", targetFilter = "all" } = {}) {
    return (threads ?? []).filter(matchesFilters)

    function matchesFilters(comment) {
        return matchesStatus(comment) && matchesTarget(comment)
    }

    function matchesStatus(comment) {
        if (statusFilter === "open") return !comment.resolved
        if (statusFilter === "resolved") return !!comment.resolved
        return true
    }

    function matchesTarget(comment) {
        if (targetFilter === "all") return true
        return getTargetValue(comment?.anchor) === targetFilter
    }
}

// Group threads by the calendar day of their latest activity (modified, falling back to created).
// Groups are ordered oldest-day -> newest-day so the feed reads top-to-bottom like a chat log,
// and threads within a day are ordered oldest-first for the same reason.
export function getDayGroups(threads = []) {
    const groups = new Map()
    for (const thread of threads ?? []) {
        const activity = getActivityDate(thread)
        if (!activity) continue
        const key = format(activity, "yyyy-MM-dd")
        if (!groups.has(key)) {
            groups.set(key, { key, label: getDayLabel(activity), date: activity, threads: [] })
        }
        groups.get(key).threads.push(thread)
    }
    const ordered = [...groups.values()].sort((a, b) => a.date - b.date)
    for (const group of ordered) {
        group.threads.sort((a, b) => getTime(a) - getTime(b))
    }
    return ordered.map(stripDate)
}

function stripDate(group) {
    return { key: group.key, label: group.label, threads: group.threads }
}

function getTime(thread) {
    const activity = getActivityDate(thread)
    return activity ? activity.getTime() : 0
}

function getActivityDate(thread) {
    const value = thread?.modified ?? thread?.created
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date
}

function getDayLabel(date) {
    if (isToday(date)) return "Today"
    if (isYesterday(date)) return "Yesterday"
    return format(date, "d MMM yyyy")
}
